import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Mic, MicOff, StopCircle, ChevronLeft, FileText, Loader2 } from "lucide-react";
import { SiriWave } from "@/components/SiriWave";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useDuplexAudio } from "@/hooks/useDuplexAudio";
import { cn } from "@/lib/utils";

interface LocationState {
  company: string;
  jobDescription?: string;
  resumeText?: string;
}

export default function Interview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { company, jobDescription, resumeText } = (location.state as LocationState) || {};

  const {
    status,
    isStartButtonDisabled,
    connectDuplexWebSocket,
    startStreaming,
    stopStreaming,
    getSortedConversation,
    cleanupAudioAndConnections,
    isSystemReady
  } = useDuplexAudio({
    jobDescription,
    resumeText
  });

  // Connect to WebSocket when component mounts and cleanup on unmount
  useEffect(() => {
    connectDuplexWebSocket();
    
    // Cleanup function for when component unmounts
    return () => {
      stopStreaming();
      cleanupAudioAndConnections();
    };
  }, []);

  const handleStartRecording = async () => {
    startStreaming();
  };

  const handleStopRecording = () => {
    stopStreaming();
  };

  const handleStopInterview = () => {
    stopStreaming();
    cleanupAudioAndConnections();
    navigate("/");
  };

  const messages = getSortedConversation();

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-background/50 to-background">
      {/* Processing Modal */}
      <AnimatePresence>
        {!isSystemReady && status === 'Connected' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm mx-auto"
            >
              <Card className="p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-blue-600/10 animate-pulse" />
                <div className="relative flex flex-col items-center text-center space-y-4">
                  <div className="relative">
                    <motion.div 
                      className="absolute -inset-4 rounded-full bg-primary/20 blur-xl"
                      animate={{ 
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 0.8, 0.5]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    <div className="relative bg-primary/10 p-4 rounded-full">
                      <FileText className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-xl font-semibold">Processing Resume</h2>
                    <p className="text-sm text-muted-foreground">
                      Please wait while we analyze your resume and prepare for the interview
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-primary">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm font-medium">Processing...</span>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleStopInterview}
              className="hover:bg-destructive/10 hover:text-destructive"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold">AI Interview Session</h1>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className={cn(
                  "h-2 w-2 rounded-full",
                  status === 'Connected' ? "bg-green-500" : "bg-red-500"
                )} />
                <span>{status}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleStopInterview}
              className="hidden sm:flex"
            >
              <StopCircle className="mr-2 h-4 w-4" />
              End Interview
            </Button>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="h-full pt-16 pb-40 overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div className="space-y-4 px-2 sm:px-4 py-4 sm:py-6 mx-auto max-w-4xl">
            <AnimatePresence initial={false}>
              {messages.map((message, index) => (
                <motion.div
                  key={message.timestamp}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: message.type === 'user' ? 100 : -100 }}
                  className={cn(
                    "flex gap-2 sm:gap-4",
                    message.type === 'user' ? "justify-end" : "justify-start"
                  )}
                >
                  {message.type !== 'user' && (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      AI
                    </div>
                  )}
                  <div className={cn(
                    "group relative rounded-2xl px-3 py-2 sm:px-4 sm:py-3 text-sm",
                    "max-w-[88%] sm:max-w-[75%]",
                    message.type === 'user' 
                      ? "bg-primary text-primary-foreground ml-8 sm:ml-12" 
                      : "bg-muted/50 backdrop-blur-sm mr-8 sm:mr-12"
                  )}>
                    <div className="mb-1 font-medium">
                      {message.type === 'user' ? 'You' : 'AI Interviewer'}
                    </div>
                    <p className="leading-relaxed whitespace-pre-wrap break-words">
                      {message.text}
                    </p>
                    <div className={cn(
                      "absolute bottom-1 right-2 text-[10px] opacity-0 transition-opacity",
                      "group-hover:opacity-70",
                      message.type === 'user' ? "text-primary-foreground" : "text-muted-foreground"
                    )}>
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                  {message.type === 'user' && (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground flex-shrink-0">
                      You
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Audio Controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-background to-background/80 backdrop-blur-lg border-t">
        <div className="container mx-auto px-4">
          <div className="relative flex flex-col items-center justify-center h-40">
            <div className="absolute left-1/2 -translate-x-1/2 w-full max-w-md">
              <SiriWave isActive={isStartButtonDisabled} />
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="z-10"
            >
              <Button
                variant={isStartButtonDisabled ? "default" : "destructive"}
                size="icon"
                className={cn(
                  "h-16 w-16 rounded-full shadow-lg transition-transform duration-200",
                  isStartButtonDisabled ? "hover:scale-95" : "hover:scale-105"
                )}
                onClick={isStartButtonDisabled ? handleStopRecording : handleStartRecording}
                disabled={status !== 'Connected'}
              >
                {isStartButtonDisabled ? <Mic className="h-8 w-8" /> : <MicOff className="h-8 w-8" />}
              </Button>
            </motion.div>
            <p className="mt-4 text-sm text-muted-foreground">
              {isStartButtonDisabled
                ? "Click to stop recording"
                : "Click to start speaking"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}